'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { EGrammarType } from '@quesmed/types';
import {
  EBatchType,
  EContentType,
  EDifficultyType,
  EMockTestType,
  EProductType,
  ETopicType,
} from '@quesmed/types/models';
import {
  type IValidateMockTestData,
  type IValidateMockTestVar,
  VALIDATE_MOCKTEST,
} from '@quesmed/types/resolvers/mutation/admin';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { GrCloudUpload } from 'react-icons/gr';
import { z } from 'zod';
import {
  contentTypes,
  getOptions,
  grammarOptions,
  mockProductMapping,
  mockTestTypes,
  productOptions,
  topicOptions,
} from '@/config';
import { ErrorFeedback, Heading, SuccessFeedback } from '@/core/molecules';
import {
  Alert,
  AnimatedComponent,
  Button,
  cn,
  FormCheckBox,
  FormFileInput,
  FormInput,
  FormSelect,
  type FormSelectProps,
  FormTextArea,
} from '@/core/ui';
import { useMutation } from '@/libs';
import { useGlobal } from '@/store';
import { fileSchema, forcedChecksSchema, forcedInsertsSchema, overWriteSchema, stringToArr } from '@/utils';

// enum EQuestionMode {
//   NORMAL = '0',
//   UKMLA = '1',
// }

const schema = z.object({
  folders: z.string().min(1, 'Folders are required'),
  title: z.string().min(1, 'Title is required'),
  intro: z.string(),
  passingMark: z.number().min(0, 'Passing mark must be greater than zero'),
  contentType: z.enum(EContentType, { message: 'Invalid enum value' }),
  topicTypeId: z.enum(ETopicType, { message: 'Invalid enum value' }),
  mockTypeId: z.enum(EMockTestType, { message: 'Invalid enum value' }),
  productId: z.enum(EProductType, { message: 'Invalid enum value' }),
  // .or(z.enum(EOsceType, { message: 'Invalid EOsceType enum value' }))
  // .or(z.enum(EPaceType, { message: 'Invalid EPaceType enum value' })),
  grammarType: z.enum(EGrammarType, { message: 'Invalid enum value' }),
  difficulty: z.enum(EDifficultyType, { message: 'Invalid enum value' }).default(EDifficultyType.CORE),
  forcedChecks: forcedChecksSchema.optional(),
  forcedInserts: forcedInsertsSchema.optional(),
  comment: z.string(),
  file: fileSchema,
  editMode: z.boolean().optional(),
  overWrite: overWriteSchema
    .nullable()
    .optional()
    .transform((val) => val ?? null),
});

type ISchema = z.input<typeof schema>;

const ValidateMockTest = () => {
  const dbType = useGlobal.use.dbType();
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      comment: '',
      difficulty: EDifficultyType.CORE,
      contentType: EContentType.MARKDOWN,
      forcedChecks: {
        concept: false,
        question: false,
        station: false,
        topic: false,
        condition: false,
        presentation: false,
        ukmlaTopic: false,
        theme: false,
      },
      forcedInserts: {
        concept: false,
        question: false,
        station: false,
        topic: false,
        condition: false,
        presentation: false,
        ukmlaTopic: false,
        theme: false,
      },
      overWrite: null,
    },
  });
  const [validateMockTest, { loading, data }] = useMutation<IValidateMockTestData, IValidateMockTestVar>(
    VALIDATE_MOCKTEST,
  );

  const finalData = useMemo(() => data?.admin?.validateMockTest, [data?.admin?.validateMockTest]);

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    try {
      delete input.editMode;
      const { error } = await validateMockTest({
        variables: { input: { ...input, folders: stringToArr(input.folders) } as any },
      });

      if (error) {
        throw new Error();
      }
      // resetField('file');
    } catch (_error) {
      // resetField('file');
    }
  };

  const handleCategoryChange: FormSelectProps<ISchema>['onValueChange'] = (value) => {
    const mapping = value ? mockProductMapping[value as EProductType] : null;
    if (!mapping) {
      return;
    }
    setValue('topicTypeId', ETopicType.MOCK_TEST);
    setValue('mockTypeId', mapping.mockTypeId);
    setValue('grammarType', mapping.grammarType);
  };

  const formData = useMemo(() => {
    return {
      topic: getOptions(topicOptions[EBatchType.MOCK_TEST], dbType),
      product: getOptions(productOptions[EBatchType.MOCK_TEST], dbType),
      grammar: getOptions(grammarOptions[EBatchType.MOCK_TEST], dbType),
      mocks: getOptions(mockTestTypes, dbType),
    };
  }, [dbType]);

  return (
    <div className='flex flex-col space-y-2 justify-center w-full items-center'>
      <div className='bg-background rounded-lg shadow-md container z-[2]'>
        <Heading title='Mock test' />

        <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10 md:pt-6'>
          <Alert
            type='warn'
            className='mb-8'
            message='Please read below before proceeding!'
            list={[
              'Make sure correct DB storage is selected',
              'Make sure template uploaded is in correct format as needed',
              'If mock test exist with provided name, then new mock test will not be created and questions will be appended',
              'Do not forgot to turn on overWrite mode in case of updating existing data',
              'By default, imported content will be hidden, you can make it visible under `Content > Batch > Public` menu',
            ]}
          />
          <div className='grid grid-cols-9 items-start gap-y-6 md:gap-y-8 gap-x-8'>
            <FormTextArea
              isRequired={true}
              disableAutosize
              minRows={2}
              description=''
              control={control}
              name='title'
              labelPlacement='outside'
              placeholder='Name of mock test'
              className='rounded col col-span-9 md:col-span-3'
              variant='faded'
              label='Title'
            />
            <FormTextArea
              isRequired={true}
              disableAutosize
              minRows={2}
              description='Enter comma separated folders eg:- Folder, Folder2'
              control={control}
              name='folders'
              labelPlacement='outside'
              placeholder='Folder1, Folder2'
              className='rounded col col-span-9 md:col-span-3'
              variant='faded'
              label='Folders'
            />
            <FormTextArea
              disableAutosize
              minRows={2}
              description='Comment/Information to store for this batch'
              control={control}
              name='comment'
              labelPlacement='outside'
              placeholder='Type Here..'
              className='rounded col col-span-9 md:col-span-3'
              variant='faded'
              label='Comment'
            />

            <FormSelect
              isRequired={true}
              description='Select category for product'
              sections={formData.product}
              options={[]}
              // description="This is basically type of product category"
              control={control}
              onValueChange={handleCategoryChange}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              name='productId'
              placeholder='Select Product Category'
              className='rounded col col-span-9 md:col-span-3 lg:col-span-3'
              variant='faded'
              label='Product Category'
            />
            <FormSelect
              isRequired={true}
              sections={formData.mocks}
              options={[]}
              description='This is basically type of mock test'
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              placeholder='Select MockTest Type'
              name='mockTypeId'
              className='rounded col col-span-9 md:col-span-3 lg:col-span-3'
              variant='faded'
              label='MockTest Type'
            />
            <FormSelect
              isRequired={true}
              options={[]}
              sections={formData.topic}
              // description="This is basically type of topic"
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              name='topicTypeId'
              labelPlacement='outside'
              placeholder='Select Topic Category'
              className='rounded col col-span-9 md:col-span-3 lg:col-span-3'
              variant='faded'
              label='Topic Type'
            />
            <FormInput
              isRequired={true}
              // description="This is basically type of question"
              control={control}
              labelPlacement='outside'
              type='number'
              transform={(a) => Number.parseInt(a, 10)}
              placeholder='Enter passing mark'
              name='passingMark'
              className='rounded col col-span-9 md:col-span-3 lg:col-span-3'
              variant='faded'
              label='Passing mark'
            />
            <FormSelect
              isRequired={true}
              options={[]}
              sections={formData.grammar}
              // description="This is basically type of question"
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              labelPlacement='outside'
              placeholder='Select Template'
              name='grammarType'
              className='rounded col col-span-9 md:col-span-3 lg:col-span-3'
              variant='faded'
              label='Template Type'
            />
            <FormSelect
              isRequired={true}
              options={contentTypes}
              // description="This is basically type of topic"
              control={control}
              transform={(a) => Number.parseInt(a, 10)}
              name='contentType'
              labelPlacement='outside'
              placeholder='Select Input Content Type'
              className='rounded col col-span-9 md:col-span-3 lg:col-span-3'
              variant='faded'
              label='Content Type'
            />
            <FormTextArea
              // disableAutosize
              minRows={6}
              description='Intro for mock test, if not passed it will bind automatically'
              control={control}
              name='intro'
              labelPlacement='outside'
              placeholder='#### Pre-examination guidance'
              className='rounded h-full col-span-9 md:col-span-3 lg:col-span-3'
              variant='faded'
              label='Intro'
            />
            <FormFileInput
              isRequired={true}
              description='Please upload only zip files'
              className='col-span-9 lg:col-span-3 h-full'
              control={control}
              name='file'
              label='Zip File'
            >
              <GrCloudUpload className='text-foreground w-14 h-14' />
            </FormFileInput>

            <div className='flex flex-col space-y-1 col-span-9 md:col-span-3 lg:col-span-3 w-full h-full'>
              <span className='text-sm text-foreground pb-1'>Force Actions</span>
              <div
                className={cn(
                  'flex flex-col justify-between rounded-md h-full',
                  'border-2 border-default-200 p-4',
                )}
              >
                <p className='text-danger text-sm mb-6'>
                  Use it when you want to force new insertion if they don't exist in db
                </p>

                <div className='flex flex-col'>
                  {/* <FormCheckBox control={control} name='forcedChecks.question'>
                    Force Question
                  </FormCheckBox> */}
                  <FormCheckBox
                    classNames={{ label: 'text-sm' }}
                    className='py-4'
                    control={control}
                    name='forcedChecks.topic'
                  >
                    Force Topic
                  </FormCheckBox>
                  <FormCheckBox
                    classNames={{ label: 'text-sm' }}
                    control={control}
                    name='forcedChecks.concept'
                  >
                    Force Concept
                  </FormCheckBox>
                </div>
              </div>
            </div>

            {/* <div
              className={cn('flex flex-col space-y-1 col-span-9 md:col-span-3 lg:col-span-3 w-full h-full')}
            >
              <FormCheckBox
                classNames={{ label: 'text-foreground text-sm' }}
                name='editMode'
                size='sm'
                control={control}
                className='rounded'
              >
                Overwrite Data
              </FormCheckBox>
              <div className={cn('h-full rounded-md', 'border-2 border-default-200 p-4')}>
                {watch('editMode') ? (
                  <div className='flex flex-col space-y-4 lg:space-y-0 h-full justify-between'>
                    <FormInput
                      // description='Enter Previous BatchId'
                      control={control}
                      name='overWrite.batchId'
                      labelPlacement='outside'
                      placeholder='04fe9191-24da-47bc-80d5-61f5089337ab'
                      className='rounded'
                      variant='faded'
                      label='Batch Id'
                    />

                    <div className='flex flex-col'>
                      <FormCheckBox
                        classNames={{ label: 'text-sm' }}
                        name='overWrite.resetLikes'
                        control={control}
                        className='rounded'
                      >
                        Reset Likes
                      </FormCheckBox>

                      <FormCheckBox
                        classNames={{ base: 'py-4', label: 'text-sm' }}
                        name='overWrite.resetProgress'
                        control={control}
                        className='rounded'
                      >
                        Reset Progress
                      </FormCheckBox>
                    </div>
                  </div>
                ) : (
                  <div className='text-sm text-danger'>
                    Enable this action only when you want to overwrite the existing questions with new data
                    that is in zip file, Also make sure the questionId specified are correct
                  </div>
                )}
              </div>
            </div> */}
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              isLoading={loading}
              className='w-full max-w-xl bg-primary'
              type='submit'
              radius='sm'
              size='lg'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
      <AnimatedComponent>
        {finalData && (
          <motion.div
            initial={{ y: -160 }}
            animate={{ y: 0 }}
            exit={{ y: -160 }}
            transition={{ type: 'spring' }}
            className={cn(
              'flex flex-col space-y-4 justify-center items-center',
              'w-full container py-5',
              'bg-background rounded-lg shadow-md ',
            )}
          >
            {finalData.status === 200 ? (
              <SuccessFeedback overWrite={watch('editMode')} response={finalData} />
            ) : (
              <ErrorFeedback response={finalData} />
            )}
          </motion.div>
        )}
      </AnimatedComponent>
    </div>
  );
};

export default ValidateMockTest;
